import { useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Car, Plus, MailWarning, CalendarDays, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LoadingState, ErrorState, EmptyState, Card, Field, FormError, SubmitButton, ConfirmDialog, SectionTitle } from "@/components/common";
import { VehicleForm } from "@/components/pro/VehicleForm";
import { VehicleTile } from "@/pages/pro/CustomerDetail";
import { MyBookings } from "@/modules/scheduling/PortalBook";
import { WorkshopCards, WorkshopTab, VehicleHistoryButton } from "@/modules/interventions/PortalJob";
import { PortalHistoryReport, ReportPaymentReturn } from "@/modules/connect/HistoryReport";
import { BalanceCard, InvoicesTab } from "@/modules/invoices/PortalInvoices";
import { MessagesTab, PortalLatestMessages } from "@/modules/messaging/PortalMessages";
import { NextServiceCard } from "@/modules/followups/PortalFollowups";
import { ReferFriendCard } from "@/modules/referral/PortalReferral";
import { fmtTime, fmtDayLong } from "@/modules/scheduling/shared";
import { Link } from "react-router-dom";
import { SimulatedEmailNote } from "@/pages/auth/ForgotPassword";
import { usePortal } from "@/pages/portal/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

function VerifyBanner() {
  const { me } = useAuth();
  const [sent, setSent] = useState(null);
  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-verification");
      setSent(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex flex-wrap items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" data-testid="verify-email-banner">
      <MailWarning className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1 space-y-2">
        <p>{t("portal.verifyPrompt", { email: me.user.email })}</p>
        {sent && <SimulatedEmailNote simulated={sent.simulated} />}
      </div>
      <Button size="sm" variant="outline" onClick={resend} data-testid="resend-verification-button">{t("portal.resendVerification")}</Button>
    </div>
  );
}

function ProfileTab({ customer, slug, onSaved }) {
  const [f, setF] = useState({ first_name: customer.first_name, last_name: customer.last_name, phone: customer.phone || "", address: customer.address || "", comm_prefs: customer.comm_prefs || { email: true, sms: false } });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.put(`/portal/${slug}/profile`, f);
      toast.success(t("common.saved"));
      onSaved();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="portal-profile-form">
      <FormError message={error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("customers.firstName")} htmlFor="pp-first"><Input id="pp-first" required value={f.first_name} onChange={set("first_name")} data-testid="portal-profile-first-name-input" /></Field>
        <Field label={t("customers.lastName")} htmlFor="pp-last"><Input id="pp-last" value={f.last_name} onChange={set("last_name")} data-testid="portal-profile-last-name-input" /></Field>
        <Field label={t("auth.email")} hint={t("portal.emailFixed")}><Input value={customer.email} disabled data-testid="portal-profile-email" /></Field>
        <Field label={t("customers.phone")} htmlFor="pp-phone"><Input id="pp-phone" value={f.phone} onChange={set("phone")} data-testid="portal-profile-phone-input" /></Field>
        <Field label={t("portal.addressOptional")} htmlFor="pp-addr" className="sm:col-span-2"><Input id="pp-addr" value={f.address} onChange={set("address")} data-testid="portal-profile-address-input" /></Field>
      </div>
      <div className="flex flex-wrap gap-6 rounded-lg border border-slate-200 p-3 text-sm">
        <span className="font-medium text-slate-800">{t("customers.commPrefs")}</span>
        {["email", "sms", "service_reminders", "marketing"].map((k) => (
          <label key={k} className="flex items-center gap-2">
            <Switch checked={f.comm_prefs[k] ?? k === "service_reminders"} onCheckedChange={(v) => setF({ ...f, comm_prefs: { ...f.comm_prefs, [k]: v } })} data-testid={`portal-comm-${k}-switch`} />
            {t(`customers.comm.${k}`)}
          </label>
        ))}
      </div>
      <SubmitButton busy={busy} style={{ background: "var(--brand)", color: "var(--brand-contrast)" }} data-testid="portal-profile-save-button">{t("common.save")}</SubmitButton>
    </form>
  );
}

function VehiclesTab({ slug, tz }) {
  const { data, error, loading, reload } = useFetch(`/portal/${slug}/vehicles`);
  const [dlg, setDlg] = useState(null);
  const [del, setDel] = useState(null);
  const remove = async () => {
    try {
      await api.post(`/portal/${slug}/vehicles/${del.id}/archive`);
      toast.success(t("archive.removed"));
      setDel(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const addBtn = <Button onClick={() => setDlg({})} style={{ background: "var(--brand)", color: "var(--brand-contrast)" }} data-testid="portal-add-vehicle-button"><Plus className="mr-2 h-4 w-4" /> {t("vehicles.add")}</Button>;
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div>
      {data.length === 0 ? <EmptyState icon={Car} title={t("portal.noVehicles")} text={t("portal.noVehiclesText")} action={addBtn} testId="portal-vehicles-empty" /> : (
        <>
          <div className="mb-4 flex justify-end">{addBtn}</div>
          <div className="grid gap-3 sm:grid-cols-2" data-testid="portal-vehicles-list">
            {data.map((v) => <div key={v.id} className="space-y-2"><VehicleTile v={v} canEdit onEdit={() => setDlg({ vehicle: v })} onDelete={() => setDel(v)} /><VehicleHistoryButton slug={slug} vehicle={v} tz={tz} /><PortalHistoryReport slug={slug} vehicle={v} /></div>)}
          </div>
          <ReportPaymentReturn slug={slug} />
        </>
      )}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="portal-vehicle-dialog">
          <DialogHeader><DialogTitle>{dlg?.vehicle ? t("vehicles.edit") : t("vehicles.add")}</DialogTitle><DialogDescription>{t("portal.vehicleHint")}</DialogDescription></DialogHeader>
          {dlg && <VehicleForm vehicle={dlg.vehicle} basePath={`/portal/${slug}/vehicles`} onSaved={() => { setDlg(null); toast.success(t("common.saved")); reload(); }} />}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} destructive title={t("archive.portalVehicleTitle")} description={t("archive.portalVehicleText", { plate: del?.plate })} confirmLabel={t("common.remove")} onConfirm={remove} testId="portal-remove-vehicle-dialog" />
    </div>
  );
}

function NextAppointment({ slug, tz, onAll, canBook }) {
  const { data } = useFetch(`/portal/${slug}/appointments`);
  const next = (data?.appointments || []).filter((a) => ["pending", "confirmed"].includes(a.status) && new Date(a.start) > new Date()).sort((a, b) => a.start.localeCompare(b.start))[0];
  return (
    <Card testId="portal-next-appointment">
      <CalendarDays className="h-5 w-5" style={{ color: "var(--brand)" }} />
      <p className="mt-3 font-display text-lg font-bold">{t("book.next")}</p>
      {next ? <p className="mt-1 text-sm text-slate-700" data-testid="next-appointment-text">{next.service_name} · {fmtDayLong(next.start, tz)} {fmtTime(next.start, tz)} · {t(`status.${next.status}`)}</p>
        : <p className="mt-1 text-sm text-slate-600">{t("book.noUpcoming")}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        {canBook && <Button asChild size="sm" style={{ background: "var(--brand)", color: "var(--brand-contrast)" }} data-testid="portal-book-service-button"><Link to={`/g/${slug}/book`}>{t("book.cta")}</Link></Button>}
        {next && <Button size="sm" variant="outline" onClick={onAll} data-testid="portal-view-bookings">{t("book.viewAll")}</Button>}
      </div>
    </Card>
  );
}

function JoinCard({ slug, garage, onJoined }) {
  const [busy, setBusy] = useState(false);
  const join = async () => {
    setBusy(true);
    try {
      await api.post(`/portal/${slug}/join`, null, { params: { ref: sessionStorage.getItem(`gf_ref_${slug}`) || undefined } });
      sessionStorage.removeItem(`gf_ref_${slug}`);
      onJoined();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="mx-auto max-w-lg text-center" testId="portal-join-card">
      <UserPlus className="mx-auto h-8 w-8" style={{ color: "var(--brand)" }} />
      <h1 className="mt-4 text-2xl font-bold text-slate-900">{t("portal.joinTitle", { garage: garage.name })}</h1>
      <p className="mt-2 text-sm text-slate-600">{t("portal.joinText")}</p>
      <Button className="mt-6" disabled={busy} onClick={join} style={{ background: "var(--brand)", color: "var(--brand-contrast)" }} data-testid="portal-join-button">{t("portal.joinButton")}</Button>
    </Card>
  );
}

export default function PortalAccount() {
  const { garage, slug } = usePortal();
  const { me, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const { data, error, loading, reload } = useFetch(`/portal/${slug}/me`);
  const tab = params.get("tab") || "overview";

  if (me === null) return <Navigate to={`/g/${slug}/login`} replace />;
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (data.user.is_super_admin) return <Card className="mx-auto max-w-lg text-sm text-slate-600">{t("portal.adminNotCustomer")}</Card>;
  if (data.membership_status === "archived") return <Card className="mx-auto max-w-lg text-sm text-slate-700" testId="portal-archived-card">{t("archive.portalInactive", { garage: garage.name })}</Card>;
  if (data.membership_status === "none") return <JoinCard slug={slug} garage={garage} onJoined={() => { reload(); refresh(); }} />;
  if (data.membership_status === "pending_verification") {
    return (
      <Card className="mx-auto max-w-lg space-y-4" testId="portal-pending-link">
        <h1 className="text-2xl font-bold text-slate-900">{t("portal.pendingTitle")}</h1>
        <p className="text-sm text-slate-600">{t("portal.pendingText", { garage: garage.name })}</p>
        <VerifyBanner />
      </Card>
    );
  }
  const c = data.customer;
  const feats = garage.features || {};
  const tabs = ["overview", "workshop", ...(feats.quotes_and_invoices ? ["invoices"] : []), ...(feats.messaging ? ["messages"] : []), "bookings", "vehicles", "profile"];
  return (
    <div className="space-y-6" data-testid="portal-account">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--brand)" }}>{garage.name}</p>
        <h1 className="mt-1 text-3xl font-bold text-slate-900 sm:text-4xl" data-testid="portal-welcome">{t("portal.hello", { name: c.first_name })}</h1>
      </div>
      {!data.user.email_verified && <VerifyBanner />}
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="flex h-auto flex-wrap justify-start bg-white shadow-sm">
          {tabs.map((k) => <TabsTrigger key={k} value={k} data-testid={`portal-tab-${k}`}>{t(`portal.tabs.${k === "workshop" && !feats.quotes_and_invoices ? "workshopOnly" : k}`)}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview" className="mt-6 space-y-4">
          <WorkshopCards slug={slug} tz={garage.timezone} currency={garage.currency} showQuotes={Boolean(feats.quotes_and_invoices)} />
          <div className="grid gap-4 md:grid-cols-3">
            <Card testId="portal-overview-vehicles">
              <Car className="h-5 w-5" style={{ color: "var(--brand)" }} />
              <p className="mt-3 font-display text-lg font-bold">{t("portal.tabs.vehicles")}</p>
              <p className="mt-1 text-sm text-slate-600">{t("portal.vehiclesBlurb")}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => setParams({ tab: "vehicles" })} data-testid="portal-overview-vehicles-button">{t("portal.manageVehicles")}</Button>
            </Card>
            <NextAppointment slug={slug} tz={garage.timezone} onAll={() => setParams({ tab: "bookings" })} canBook={Boolean(feats.online_booking)} />
            {feats.quotes_and_invoices && <BalanceCard slug={slug} currency={garage.currency} />}
            {feats.messaging && <PortalLatestMessages slug={slug} tz={garage.timezone} />}
            {feats.referral && <ReferFriendCard slug={slug} />}
          </div>
          {feats.follow_ups && <NextServiceCard slug={slug} tz={garage.timezone} canBook={Boolean(feats.online_booking)} />}
        </TabsContent>
        <TabsContent value="workshop" className="mt-6"><Card><WorkshopTab slug={slug} tz={garage.timezone} currency={garage.currency} /></Card></TabsContent>
        <TabsContent value="invoices" className="mt-6"><Card><SectionTitle>{t("portal.tabs.invoices")}</SectionTitle><InvoicesTab slug={slug} currency={garage.currency} /></Card></TabsContent>
        <TabsContent value="messages" className="mt-6"><MessagesTab slug={slug} tz={garage.timezone} /></TabsContent>
        <TabsContent value="bookings" className="mt-6"><Card><SectionTitle>{t("portal.tabs.bookings")}</SectionTitle><MyBookings slug={slug} tz={garage.timezone} /></Card></TabsContent>
        <TabsContent value="vehicles" className="mt-6"><Card><SectionTitle>{t("portal.tabs.vehicles")}</SectionTitle><VehiclesTab slug={slug} tz={garage.timezone} /></Card></TabsContent>
        <TabsContent value="profile" className="mt-6"><Card><SectionTitle hint={t("portal.profileHint", { garage: garage.name })}>{t("portal.tabs.profile")}</SectionTitle><ProfileTab customer={c} slug={slug} onSaved={reload} /></Card></TabsContent>
      </Tabs>
    </div>
  );
}
